/* LMC Product Listing Page (PLP) behavior.
   - Auto-submit the filter form when a checkbox or sort changes (so
     the user doesn't need an explicit "apply" button on JS-capable
     browsers; the noscript fallback in the form still works).
   - Grid/list view toggle: swap data-view on the grid, persist
     preference per-session. The toggle is hidden on mobile (CSS), so
     this only drives tablet/desktop. */

(function () {
  /* Mobile + tablet (<990px): filters sit on top behind a collapsible
     <details> — collapse it at load so the grid sits above the fold. On
     desktop (≥990px) the sidebar is always open and the disclosure must be
     genuinely inert: pointer-events CSS only blocks the mouse, so keep the
     summary out of the tab order and reopen if anything (keyboard, AT)
     closes it. */
  const filtersDetails = document.querySelector('[data-lmc-filters-details]');
  if (filtersDetails) {
    const summary = filtersDetails.querySelector('summary');
    const mobile = window.matchMedia('(max-width: 989px)');

    const applyViewport = () => {
      if (mobile.matches) {
        if (summary) summary.removeAttribute('tabindex');
      } else {
        filtersDetails.setAttribute('open', '');
        if (summary) summary.setAttribute('tabindex', '-1');
      }
    };

    if (mobile.matches) filtersDetails.removeAttribute('open');
    applyViewport();
    mobile.addEventListener('change', applyViewport);
    filtersDetails.addEventListener('toggle', () => {
      if (!mobile.matches && !filtersDetails.open) {
        filtersDetails.setAttribute('open', '');
      }
    });
  }

  const form = document.getElementById('lmc-filters-form');
  if (form) {
    /* Price inputs display money-formatted values ($10,000.00) but the
       filter.v.price.* params must be plain dollar amounts — strip the
       formatting from every price field before any submit.

       Separator handling: the last "."/"," is the decimal point only
       when 1–2 digits follow it; with 3 it's a thousands separator.
       This parses "$10,000.00", a hand-typed "12,000" (12000, not 12),
       and comma-decimal formats like "10.000,50" alike. */
    const normalizePriceInputs = () => {
      form.querySelectorAll('[data-lmc-price-input]').forEach((input) => {
        const cleaned = input.value.replace(/[^0-9.,]/g, '');
        const sep = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
        const decimals = sep === -1 ? '' : cleaned.slice(sep + 1);
        const plain =
          sep !== -1 && decimals.length >= 1 && decimals.length <= 2
            ? cleaned.slice(0, sep).replace(/[.,]/g, '') + '.' + decimals
            : cleaned.replace(/[.,]/g, '');
        const parsed = parseFloat(plain);
        input.value = Number.isFinite(parsed) ? String(parsed) : '';
      });
    };

    const submitFilters = () => {
      normalizePriceInputs();
      form.submit();
    };

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (
        target.matches('input[type="checkbox"]') ||
        target.matches('[data-lmc-price-input]')
      ) {
        submitFilters();
      }
    });

    /* The sort <select> is associated to the form via the form= attribute but
       lives outside the form in the DOM (in the toolbar), so its change event
       bubbles through the toolbar — not the form — and never reaches the
       delegated listener above. Bind it directly. It's still submitted with
       the form thanks to the form association. */
    const sortSelect = document.querySelector('select[name="sort_by"][form="lmc-filters-form"]');
    if (sortSelect) {
      sortSelect.addEventListener('change', submitFilters);
    }

    /* Enter inside a price field triggers a native submit (form.submit()
       above bypasses this event, so no double-normalizing). */
    form.addEventListener('submit', normalizePriceInputs);
  }

  /* Grid/list view toggle (hidden on mobile via CSS; drives tablet+).
     Not persisted across visits — a fresh load or refresh starts in the
     default grid view. The selection is carried across pagination only:
     when the view isn't the default we tag the pager links with ?view=,
     and on load we apply that param then strip it from the address bar so
     a genuine refresh still resets to grid. */
  const grid = document.querySelector('.lmc-pgrid');
  const viewButtons = document.querySelectorAll('.lmc-toolbar__view-btn');
  const DEFAULT_VIEW = 'grid';

  function setActiveButton(view) {
    viewButtons.forEach((b) => {
      const isActive = b.dataset.view === view;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // Reflect the current view onto pager links so a page click preserves it.
  function syncPagerLinks(view) {
    document.querySelectorAll('.lmc-pager__link[href]').forEach((a) => {
      const url = new URL(a.getAttribute('href'), window.location.href);
      if (view && view !== DEFAULT_VIEW) {
        url.searchParams.set('view', view);
      } else {
        url.searchParams.delete('view');
      }
      a.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  if (grid && viewButtons.length > 0) {
    // Apply a view carried in from a pagination click, then scrub the param
    // so refreshing this URL falls back to the default view.
    const params = new URLSearchParams(window.location.search);
    const incomingView = params.get('view');
    if (incomingView === 'grid' || incomingView === 'list') {
      grid.dataset.view = incomingView;
      setActiveButton(incomingView);
    }
    // Scrub any view param (including invalid values) so refresh resets to default.
    if (params.has('view')) {
      params.delete('view');
      const qs = params.toString();
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
      );
    }

    syncPagerLinks(grid.dataset.view);

    viewButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        grid.dataset.view = btn.dataset.view;
        setActiveButton(btn.dataset.view);
        syncPagerLinks(btn.dataset.view);
      });
    });
  }

  /* "Hide filters" toggle (desktop) — collapses the sidebar so the grid
     spans full width. Not persisted — resets to filters-shown on load. */
  const filtersToggle = document.querySelector('[data-lmc-filters-toggle]');
  const colBody = document.querySelector('.lmc-col-body');
  if (filtersToggle && colBody) {
    const label = filtersToggle.querySelector('[data-filters-toggle-label]') || filtersToggle;
    filtersToggle.addEventListener('click', () => {
      const hidden = !colBody.classList.contains('is-filters-hidden');
      colBody.classList.toggle('is-filters-hidden', hidden);
      filtersToggle.setAttribute('aria-expanded', hidden ? 'false' : 'true');
      label.textContent = hidden ? 'Show filters' : 'Hide filters';
    });
  }

})();
